<template>
    <div class="validation-page-container">
        <!-- État Chargement Initial -->
        <div v-if="isLoadingInitial" class="card loading-card">
            <LoaderComponent :text="$t('devisValidation.loading') || 'Chargement des informations du devis...'" />
        </div>

        <!-- Vue 2 : Écran de Succès -->
        <div v-else-if="isSuccess" class="card success-card">
            <div class="icon-container success-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
            </div>
            <h1 class="card-title">{{ $t('devisValidation.success.title') || 'Devis validé avec succès !' }}</h1>
            <div class="badge-devis">
                {{ $t('devisValidation.devisNumber') || 'Devis n°' }} {{ devisNumeroVersion }}
            </div>
            <p class="success-message">
                {{ $t('devisValidation.success.message') || 'La commande a été transmise. L’état Commandé sera visible dans l’outil de chiffrage sous 15 minutes maximum.' }}
            </p>
            <div class="card-footer-note">
                {{ $t('devisValidation.closeTab') || 'Vous pouvez fermer cet onglet.' }}
            </div>
        </div>

        <!-- Vue 3 : Gestion des Erreurs -->
        <div v-else-if="errorMessage" class="card error-card">
            <div class="icon-container error-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
            </div>
            <h1 class="card-title">{{ $t('devisValidation.error.title') || 'Impossible de valider le devis' }}</h1>
            <p class="error-message">{{ errorMessage }}</p>
        </div>

        <!-- Vue 1 : Page de Récapitulatif & Confirmation -->
        <div v-else-if="devisDetails" class="card recap-card">
            <div class="card-header">
                <h1 class="card-title">{{ $t('devisValidation.recap.title') || 'Validation de commande' }}</h1>
                <p class="card-subtitle">{{ $t('devisValidation.recap.subtitle') || 'Veuillez confirmer la commande de ce devis.' }}</p>
            </div>

            <div class="details-list">
                <div class="detail-row">
                    <span class="detail-label">{{ $t('devisValidation.devisNumber') || 'Devis n°' }} :</span>
                    <span class="detail-value highlight">{{ devisNumeroVersion }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">{{ $t('devisValidation.project') || 'Projet' }} :</span>
                    <span class="detail-value">{{ devisDetails.libelleProjet || '-' }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">{{ $t('devisValidation.object') || 'Objet' }} :</span>
                    <span class="detail-value">{{ devisDetails.objet || '-' }}</span>
                </div>
                <div class="detail-row">
                    <span class="detail-label">{{ $t('devisValidation.amount') || 'Montant' }} :</span>
                    <span class="detail-value amount-value">{{ formattedMontant }}</span>
                </div>
            </div>

            <!-- Message d'information si déjà validé -->
            <div v-if="devisDetails.dejaValide" class="info-box already-validated">
                <span class="info-icon">ℹ️</span>
                <span>{{ $t('devisValidation.alreadyValidated') || 'Ce devis a déjà été validé.' }}</span>
            </div>

            <div class="actions-container">
                <!-- Message d'erreur PDF dédié -->
                <div v-if="pdfErrorMessage" class="pdf-error-banner" role="alert">
                    <span>{{ pdfErrorMessage }}</span>
                </div>

                <!-- Bouton PDF si disponible -->
                <button
                    v-if="devisDetails.isPdfPresent"
                    class="btn btn-pdf"
                    :disabled="isPdfLoading"
                    @click="openPdf"
                >
                    <span class="btn-icon">📄</span>
                    <span>{{ isPdfLoading ? ($t('devisValidation.pdfLoading') || 'Chargement PDF...') : ($t('devisValidation.viewPdf') || 'Visualiser le devis (PDF)') }}</span>
                </button>

                <!-- Bouton Commander si non déjà validé -->
                <button
                    v-if="!devisDetails.dejaValide"
                    class="btn btn-confirm"
                    :disabled="isConfirming"
                    @click="confirmOrder"
                >
                    <span class="btn-icon">✔</span>
                    <span>{{ isConfirming ? ($t('devisValidation.confirming') || 'Validation en cours...') : ($t('devisValidation.confirmButton') || 'Commander le devis') }}</span>
                </button>
            </div>

            <div class="help-mention">
                {{ $t('devisValidation.helpMention') || 'Cette action passera automatiquement le devis à l’état « Commandé » dans l’outil de chiffrage.' }}
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import currencyFormatter from 'currency-formatter';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import DevisValidationService from '@/modules/DevisValidation/services/DevisValidationService';
import { DevisValidationDetailsDTO, DevisValidationResultDTO } from '@/models/DevisValidation';

export default defineComponent({
    name: 'ValidationDevisPage',
    components: {
        LoaderComponent,
    },
    data() {
        return {
            token: '' as string,
            isLoadingInitial: true as boolean,
            isConfirming: false as boolean,
            isPdfLoading: false as boolean,
            isSuccess: false as boolean,
            errorMessage: '' as string,
            pdfErrorMessage: '' as string,
            devisDetails: null as DevisValidationDetailsDTO | null,
            validationResult: null as DevisValidationResultDTO | null,
        };
    },
    computed: {
        devisNumeroVersion(): string {
            if (this.validationResult) {
                return `${this.validationResult.numeroDevis}-${this.validationResult.versionDevis}`;
            }
            if (this.devisDetails) {
                return `${this.devisDetails.numeroDevis}-${this.devisDetails.versionDevis}`;
            }
            return '';
        },
        formattedMontant(): string {
            if (this.devisDetails?.montantHt != null) {
                const formatted = currencyFormatter.format(this.devisDetails.montantHt, { code: 'EUR' });
                return `${formatted} HT`;
            }
            return '-';
        },
    },
    mounted() {
        this.initialize();
    },
    methods: {
        initialize() {
            let tokenParam: string | null = null;
            if (typeof this.$route.query.token === 'string' && this.$route.query.token.trim() !== '') {
                tokenParam = this.$route.query.token.trim();
            } else if (this.$route.hash) {
                const match = this.$route.hash.match(/[?&]token=([^&#]+)/) || this.$route.hash.match(/token=([^&#]+)/);
                if (match && match[1]) {
                    tokenParam = decodeURIComponent(match[1]).trim();
                }
            } else if (typeof window !== 'undefined' && window.location.href) {
                const match = window.location.href.match(/[?&]token=([^&#]+)/);
                if (match && match[1]) {
                    tokenParam = decodeURIComponent(match[1]).trim();
                }
            }

            if (!tokenParam) {
                this.isLoadingInitial = false;
                this.errorMessage = this.$t('devisValidation.error.missingToken') || 'Lien invalide : aucun jeton de validation fourni.';
                return;
            }

            this.token = tokenParam;
            this.fetchDetails();
        },
        async fetchDetails() {
            this.isLoadingInitial = true;
            this.errorMessage = '';
            this.pdfErrorMessage = '';

            try {
                const response = await DevisValidationService.getDetails(this.token);
                if (response?.data) {
                    this.devisDetails = response.data;
                } else {
                    this.errorMessage = this.$t('devisValidation.error.generic') || 'Une erreur est survenue lors de la validation. Veuillez réessayer ultérieurement.';
                }
            } catch (error: any) {
                this.handleHttpError(error);
            } finally {
                this.isLoadingInitial = false;
            }
        },
        async confirmOrder() {
            if (!this.token || this.isConfirming) return;

            this.isConfirming = true;
            this.pdfErrorMessage = '';
            try {
                const response = await DevisValidationService.confirmer(this.token);
                if (response?.data) {
                    this.validationResult = response.data;
                    this.isSuccess = true;
                } else {
                    this.errorMessage = this.$t('devisValidation.error.generic') || 'Une erreur est survenue lors de la validation. Veuillez réessayer ultérieurement.';
                }
            } catch (error: any) {
                this.handleHttpError(error);
            } finally {
                this.isConfirming = false;
            }
        },
        async openPdf() {
            if (!this.token || this.isPdfLoading) return;

            this.isPdfLoading = true;
            this.pdfErrorMessage = '';

            const newWindow = window.open('', '_blank');

            try {
                const response = await DevisValidationService.getPdf(this.token);
                if (response?.data) {
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const fileUrl = window.URL.createObjectURL(blob);
                    if (newWindow) {
                        newWindow.location.href = fileUrl;
                    } else {
                        window.open(fileUrl, '_blank');
                    }
                    setTimeout(() => {
                        window.URL.revokeObjectURL(fileUrl);
                    }, 60000);
                } else {
                    if (newWindow) {
                        newWindow.close();
                    }
                    this.pdfErrorMessage = this.$t('devisValidation.error.pdf') || 'Impossible de charger le document PDF.';
                }
            } catch (error: any) {
                console.error('Erreur lors de l’ouverture du PDF', error);
                if (newWindow) {
                    newWindow.close();
                }
                this.pdfErrorMessage = this.$t('devisValidation.error.pdf') || 'Impossible de charger le document PDF.';
            } finally {
                this.isPdfLoading = false;
            }
        },
        handleHttpError(error: any) {
            const status = error?.response?.status;
            if (status === 410) {
                this.errorMessage = this.$t('devisValidation.error.expired') || 'Ce lien a expiré (durée de validité de 90 jours dépassée). Veuillez vous connecter à l\'outil de chiffrage.';
            } else if (status === 404) {
                this.errorMessage = this.$t('devisValidation.error.notFound') || 'Devis introuvable.';
            } else if (status === 400) {
                this.errorMessage = this.$t('devisValidation.error.missingToken') || 'Lien invalide : aucun jeton de validation fourni.';
            } else {
                this.errorMessage = this.$t('devisValidation.error.generic') || 'Une erreur est survenue lors de la validation. Veuillez réessayer ultérieurement.';
            }
        },
    },
});
</script>

<style scoped>
.validation-page-container {
    width: 100%;
    max-width: 620px;
    margin: 40px auto;
    padding: 0 16px;
    box-sizing: border-box;
}

.card {
    background-color: var(--white);
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    padding: 36px 32px;
    border: 1px solid var(--ombre-contour);
}

.loading-card {
    text-align: center;
    padding: 60px 32px;
}

/* Card Header */
.card-header {
    text-align: center;
    margin-bottom: 28px;
}

.card-title {
    color: var(--color-principal);
    font-family: 'Raleway Semi Bold', 'Raleway', sans-serif;
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-bold);
    margin: 0 0 8px 0;
}

.card-subtitle {
    color: var(--medium-dark-grey);
    font-size: var(--font-size-low-medium);
    margin: 0;
}

/* Details list */
.details-list {
    background-color: var(--background);
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: var(--font-size-small);
    border-bottom: 1px dashed var(--medium-light-grey);
    padding-bottom: 10px;
}

.detail-row:last-child {
    border-bottom: none;
    padding-bottom: 0;
}

.detail-label {
    color: var(--color-principal);
    font-weight: var(--font-weight-600);
}

.detail-value {
    color: var(--dark-grey);
    font-weight: var(--font-weight-medium);
    text-align: right;
    max-width: 60%;
    word-break: break-word;
}

.detail-value.highlight {
    color: var(--bleu-secondaire);
    font-weight: var(--font-weight-bold);
    font-family: 'Raleway Semi Bold', sans-serif;
}

.detail-value.amount-value {
    color: var(--color-principal);
    font-size: var(--font-size-low-medium);
    font-weight: var(--font-weight-bold);
}

/* Info Box */
.info-box {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 18px;
    border-radius: 8px;
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
    margin-bottom: 24px;
}

.info-box.already-validated {
    background-color: #e8f4fd;
    color: #0b63b7;
    border: 1px solid #bce1fe;
}

.info-icon {
    font-size: 18px;
}

/* Actions */
.actions-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 20px;
}

.pdf-error-banner {
    background-color: #fee;
    color: #c53030;
    border: 1px solid #feb2b2;
    border-radius: 6px;
    padding: 10px 14px;
    font-size: var(--font-size-very-small);
    font-weight: var(--font-weight-medium);
    text-align: center;
}

.btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 46px;
    border-radius: 8px;
    font-size: var(--font-size-small);
    font-family: 'Raleway Semi Bold', 'Raleway', sans-serif;
    font-weight: var(--font-weight-bold);
    cursor: pointer;
    transition: background-color 0.2s ease, opacity 0.2s ease;
    border: none;
    outline: none;
    width: 100%;
}

.btn:disabled {
    cursor: not-allowed;
    opacity: 0.65;
}

.btn-icon {
    font-size: 16px;
}

.btn-pdf {
    background-color: var(--white);
    color: var(--bleu-secondaire);
    border: 1px solid var(--bleu-secondaire);
}

.btn-pdf:hover:not(:disabled) {
    background-color: #f0f3ff;
}

.btn-confirm {
    background-color: #29D68B;
    color: var(--white);
}

.btn-confirm:hover:not(:disabled) {
    background-color: #23bd7a;
}

/* Help mention */
.help-mention {
    text-align: center;
    font-size: var(--font-size-very-small);
    color: var(--medium-dark-grey);
    font-style: italic;
    line-height: 1.4;
}

/* Vue Succès */
.success-card {
    text-align: center;
    padding: 48px 32px;
}

.icon-container {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    border-radius: 50%;
    margin-bottom: 20px;
}

.success-icon {
    background-color: #e8fbf3;
    color: #29D68B;
}

.badge-devis {
    display: inline-block;
    background-color: var(--background-principal);
    color: var(--bleu-secondaire);
    font-weight: var(--font-weight-bold);
    padding: 6px 16px;
    border-radius: 20px;
    font-size: var(--font-size-small);
    margin: 12px 0 20px 0;
}

.success-message {
    color: var(--color-principal);
    font-size: var(--font-size-low-medium);
    line-height: 1.5;
    margin: 0 0 28px 0;
}

.card-footer-note {
    font-size: var(--font-size-small);
    color: var(--medium-dark-grey);
    font-weight: var(--font-weight-medium);
}

/* Vue Erreur */
.error-card {
    text-align: center;
    padding: 48px 32px;
}

.error-icon {
    background-color: #fee2e2;
    color: #dc2626;
}

.error-message {
    color: var(--color-principal);
    font-size: var(--font-size-low-medium);
    line-height: 1.5;
    margin: 16px 0 0 0;
}

@media (max-width: 600px) {
    .card {
        padding: 24px 18px;
    }

    .detail-row {
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
    }

    .detail-value {
        text-align: left;
        max-width: 100%;
    }
}
</style>
